

import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { getFilterCount, getFilters } from "core/hooks/filters";
import { getAnalystDashCasesGridBoxData, getAnalystDashAlertTrends } from "modules/ac2/utils/alertUtils";
import { AuthContext } from "core/contexts/auth/auth";
import { 
  setAlertStatus, 
  setAlertsByPage, 
  setAreUserEventsLoading, 
  setSelectedRow, 
  setSideModal, 
  setWeeklyAlerts, 
  setConformanceData
} from "modules/ac2/store/alert";

import { Toast as showToast } from "core/components/global/toast/toast";


import GridPageLayout from "core/components/layout/grid/gridLayout/gridPageLayout";
import Datagrid from "core/components/global/tables/datagrid/dataGrid";
import { DashboardColumn, DashboardItem } from "core/components/layout/dashboards/twoColumns/dashboard";
import { StyledDashContainer } from "core/components/layout/dashboards/twoColumns/dashboard.styled";

import CircleIcon from "@mui/icons-material/Circle";
import { Box } from "@mui/material";

import { useAppDispatch, useAppSelector } from "core/store/reduxStore";
import { getHomeMetrics } from "modules/ac2/services/alertService";
import StackedAreaChart from "./charts/stackedAreaChart";
import ChartLegend from "core/components/global/legend/chartLegend";
import HorizontalBarChart from "./charts/horizontalBarChart";
import CyberIntelligence from "./cyberIntelligence/cyberIntelligence";
import CustomPieChart from "./charts/customPieChart";
import { useTheme } from "styled-components";
import { ISortData } from "../ac2Alerts/alertsTable";
import AnalystToolbar from "./analystToolbar";
import { CompliancePieCharts } from "./charts/complianceChart";
import AlertSidePanel from "../ac2Alerts/alertSidePanel";

interface activity { 
  description: string; count: number; circleColor?: string; icon?: any; percentage: number;
}

interface urgency { 
  description: string; count: number; circleColor?: string; 
}

interface trendCriticality { 
  color: string; count: number;
}

interface trends { 
  name: string; low: trendCriticality; medium: trendCriticality, high: trendCriticality
}

const gridBoxData = [
  { num: 0, type: "Assigned to me", color: "fff" },
  { num: 0, type: "Unassigned", color: "fff" },
  { num: 0, type: "Critical alerts today", color: "fff" },
];

// Source these from somewhere
const threat_feeds = [
  {"Campaign": "NotPetya", "Date": "2024-02-25"},
  {"Campaign": "Sodinokibi", "Date": "2024-02-10"},
  {"Campaign": "Zeus", "Date": "2024-02-14"},
  {"Campaign": "TrickBot", "Date": "2024-01-08"},
  {"Campaign": "Emotet", "Date": "2023-12-22"},
  {"Campaign": "DarkTequila", "Date": "2023-11-18"},
  {"Campaign": "WannaCry", "Date": "2023-10-30"},
  {"Campaign": "Ryuk", "Date": "2023-10-15"},
  {"Campaign": "Maze", "Date": "2023-12-20"},
  {"Campaign": "LockBit", "Date": "2023-11-05"}
]

const reports = [{
  "name": "Oracle Quarterly Critical Patch Update: January 2024"
}, {
  "name": "Cisco Releases Multiple Advisories to Fix 32 Vulnerabilities Affecting Multiple Products"
}, {
  "name": "Google Releases New Version of Android to Address 78 Vulnerabilities in Google Android"
}, {
  "name": "Microsoft Security Bulletin Review for November 2023"
}, {
  "name": "Google Releases New Version of Chrome (119.0.6045.199) for Mac and Linux (119.0.6045.199/.200) and for Windows to Fix 6 Vulnerabilities"
}, {
  "name": "Google Releases New Version of Chrome (120.0.6099.62) for Mac and Linux (120.0.6099.62/.63) and for Windows to Fix 5 Vulnerabilities"
}, {
  "name": "Iran and Russia Poised for More Cyber Cooperation"
}, {
  "name": "Cyber Threats to ICS Environments from 2022 to Early 2023"
}]


interface Params {
    target: string;
    aFAccessToken: string;
    page: number;
    size: number;
    orderBy: string;
    order: string;
    filters: object;
    search: string;
}
  
/**
 * This component tries to separate out the related components
 * that do not have to do with the Redux list of alerts and 
 * those that do, this is so that only the affected components
 * are refreshed when data is added or changed.
 * @returns 
 */
export const AnalystTable = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const alertState = useAppSelector((state) => state.alerts)
    const openSideModal = useAppSelector((state) => state.alerts.openSideModal);
    console.log("AnalystTable: openSideModal", openSideModal);

    const status_color_mapping = {
      [t("STATUS.OPEN")]: theme.colors.open_incident,
      [t("STATUS.IN_PROGRESS")]: theme.colors.in_progress_incident,
      [t("STATUS.CLOSED")]: theme.colors.closed_incident,
      [t("STATUS.REQUEST_TUNING")]: theme.colors.requested_incident
    }

    const [page, setPage] = useState(0);
    const [gridBox, setGridBox] = useState(gridBoxData);
    const [casesByUrgency, setCasesByUrgency] = useState<urgency[]>([]);
    const [weekServiceActivity, setWeekServiceActivity] = useState<activity[]>([]);
    const [allServiceActivity, setAllServiceActivity] = useState<activity[]>(
      [
        {
          "description":t("STATUS.OPEN"),"count":1,"circleColor":status_color_mapping[t("STATUS.OPEN")],"percentage":0},
          {"description":"In Progress","count":0,"circleColor":status_color_mapping["In Progress"],"percentage":0},
          {"description":t("STATUS.CLOSED"),"count":0,"circleColor":status_color_mapping[t("STATUS.CLOSED")],"percentage":0},
          {"description":"Request Tuning","count":0,"circleColor":status_color_mapping["Request Tuning"],"percentage":0}]
    );
    const [alertTrends, setAlertTrends] = useState<trends[]>([]);
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
    const { amsUser } = useContext(AuthContext);
    const dispatch = useAppDispatch();
    const { checkAuthToken } = useContext(AuthContext)
    const [pageSize, setPageSize] = useState(5);
    const [sortData, setSortData] = useState<ISortData>({
      field: "alertId",
      order: "desc",
    });
  
    const statusMapping = {
      [t("STATUS.OPEN")]: theme.colors.open_incident,
      [t("STATUS.IN_PROGRESS")]: theme.colors.in_progress_incident,
      [t("STATUS.CLOSED")]: theme.colors.closed_incident,
      [t("STATUS.REQUEST_TUNING")]: theme.colors.requested_incident
    }

    /**
     * Handle the expanded alert modal
     * @param id 
     */
    const onAlertClick = async (id) => { 
      console.log(`onAlertClick: ${id}`)   
        if (id) {
            dispatch(setSelectedRow(id));
            dispatch(setSideModal(true));
        }
    };

    /**
     * Handle filter data from the table
     */
    const userCaseState = useAppSelector((state) => state.alerts);
    const [filterData, setFilterData] = useState({ "totalFilters": 0, "filterTerms": {}, "searchTerm": ""});

    async function handleFilterData({
        filter, 
        argPageSize = pageSize, 
        argPageNum = page,
        argSortField = sortData.field, 
        argSortOrder = sortData.order
      }) {
      const token = await checkAuthToken();
  
      const params: Params = {
        target: "home",
        aFAccessToken: token, 
        page: argPageNum,
        size: argPageSize,
        orderBy: argSortField,
        order: argSortOrder,
        filters: filterData["filterTerms"],
        search: filterData["searchTerm"]
      }
      
      // A new filter has been provided, parse an add replace the existing
      params.filters = getFilters(filter, params);

      // As this is the user home only make the request if the user can be found
      if (amsUser) {
        // Add the user's email as a filter
        params.filters["filter[assignedto]"] = amsUser.email;
        
        // Send the request to the backend
        dispatch(setAlertsByPage(params));
        
        // Save the current filter for the next request
        setFilterData({
          totalFilters: getFilterCount(filterData["searchTerm"], params.filters),
          filterTerms: params.filters,
          searchTerm: filterData["searchTerm"]
        })
      } else {
        // TODO: implement logout user
        console.error("Cannot find user")
      }
    }
  
    async function deleteFilter(key, value) {
      const token = await checkAuthToken();
  
      const tempTerms: { [key: string]: any } = filterData["filterTerms"]
      const filterValues = String(tempTerms[key]);
  
      if (filterValues) {
        if (filterValues.includes(",")) {
          // When filters contain multiple values per Filter Type
          tempTerms[key] = filterValues.split(',').filter(item => item !== value).join(',');
        } else {
          tempTerms[key] = ""
        }
  
        for (const key in tempTerms) {
          if (tempTerms[key] === "") {
            delete tempTerms[key];
          }
        }
    
        setFilterData(prev => ({
          filterTerms: tempTerms,
          totalFilters: getFilterCount(filterData["searchTerm"], tempTerms),
          searchTerm: filterData["searchTerm"]
        }));
  
        dispatch(
          setAlertsByPage(
            {
              target: "home",
              aFAccessToken: token, 
              page: page,
              size: pageSize,
              orderBy: sortData.field,
              order: sortData.order,
              filters: tempTerms ? tempTerms : filterData["filterTerms"],
              search: filterData["searchTerm"]
            }
          )
        );
      }
    }

    async function onSortChange(field: string, order: string) {
        setSortData({ field, order });
        handleFilterData({
          filter: {}, 
          argSortOrder: order, 
          argSortField: field === "id" ? "alertId" : field
        });
    }
    
    async function onPageChange(pageNum) {
        handleFilterData({filter: {}, argPageNum: pageNum});
    }
    
    async function handleRowsPerPageChange(pageSize) {
        setPageSize(pageSize)
        handleFilterData({filter: {}, argPageSize: pageSize});
    }

    /**
     * Set the status of an alert
     * @param status 
     * @param id 
     */
    const handleSetStatus = async (status: string, id: string) => {
        if (status) {
            const token = await checkAuthToken();
            dispatch(setAlertStatus({'aFAccessToken': token, 'status': status, 'id': id}));
        }
    }

    /**
     * Fetch the user's associated alerts
     */
    const fetchUserAlertsByPage = async () => {
        console.log("My data: Fetching user alerts by page", page, pageSize, sortData, filterData);
        const token = await checkAuthToken();        
        dispatch(
          setAlertsByPage(
            {
              target: "home",
              aFAccessToken: token, 
              page: page,
              size: pageSize,
              orderBy: sortData.field,
              order: sortData.order,
              filters: {},
              search: filterData["searchTerm"]
            }
          )
        );
    };
    
    /**
     * Filter by week and day from both the user and all pageEvents to 
     * populate thw statistics gridbar at the top of the Component
     * @param pageEvents 
     */
    async function setGridData(eventsFromLast7Days) {
      const token = await checkAuthToken();
      const config = { 'Authorization': token || '', 'Content-Type': 'application/json' };
      let timeNow: number = Math.round(new Date().getTime() / 1000);

      const homeMetrics = await getHomeMetrics(config);

      if (homeMetrics) {
        // Iterate 7 day data 
        const result = getAnalystDashCasesGridBoxData(t, theme, eventsFromLast7Days, homeMetrics["totalResults"]);
        setCasesByUrgency(result.urgencyValues);
        setWeekServiceActivity(result.activityValues);

        // Alert Prioritization Status - GET STATUS Metrics
        if (homeMetrics["status"]) {
          const alertPrioritizationActivity = Object.entries(homeMetrics["status"]).map(([status, count]: [string, number]) => {
            return {description: status, count: count, circleColor: status_color_mapping[status], percentage: parseFloat(((count/homeMetrics["totalRecords"]) * 100).toFixed(2))}
          })
          setAllServiceActivity(alertPrioritizationActivity);
        }
      }

      // Get the StackedChart Data, Alerts past 7 days severity (H, M, L)
      const result2 = getAnalystDashAlertTrends(eventsFromLast7Days);
      setAlertTrends(result2 as trends[]);  

      // Check if the event is critical and the event's lastUpdated date is within the last day
      const criticalEvents = eventsFromLast7Days.filter(event => {
        if (!event.createdDate) return false; // skip if missing
        const timeThen = new Date(event.createdDate as string).getTime();
        if (isNaN(timeThen)) return false; // skip if invalid date
        let now = timeNow;
        if (now.toString().length !== timeThen.toString().length) {
          now = now * 1000;
        }
        return event.severity === "Critical" && now - timeThen <= 1 * 24 * 60 * 60 * 1000;
      });
      // Set the data for the top bar
      const tempData = gridBox.map((item) => {
        if (item.type === "Assigned to me") {
            item.num = userCaseState.userEvents.length || 0;
        } else if (item.type === "Unassigned") {
            item.num = homeMetrics && homeMetrics["unassigned"] ? homeMetrics["unassigned"] : 0
        } else if (item.type === "Critical alerts today") {
            item.num = criticalEvents.length || 0;
        }
        return item;
      });
      
      setGridBox(tempData);
    }

    useEffect(() => {      
      dispatch(setAreUserEventsLoading(true));
      const fetchData = async () => {
        // Fetch userdata
        fetchUserAlertsByPage();

        // Fetch metrics data
        const token = await checkAuthToken();
        await dispatch(setWeeklyAlerts(token));
      };
      
      // Set the interval, every X seconds 'fetchData()' will be called
      // const intervalId = setInterval(fetchData, 30000);
      
      // Once the component closes, close this interval: only fetch when in focus
      // return () => {
      //     clearInterval(intervalId);
      // };
    }, [page, pageSize, sortData, filterData]);
 
    // Once the user pageEvents list changes, update the grid
    useEffect(() => {
      setGridData(userCaseState.weeklyEvents)
    }, [userCaseState.weeklyEvents])

    async function fetchWeekData() {
      const token = await checkAuthToken();
      dispatch(setWeeklyAlerts(token))
    }

    async function fetchConformanceData() {
      const token = await checkAuthToken();
      dispatch(setConformanceData(token))
    }

    useEffect(() => {
      return () => {
        // This will clear the selected row on unmount/navigation
        dispatch(setSelectedRow(""));
      };
    }, [dispatch]);
    
    useEffect(() => {
      if(amsUser !== null) {
        // Fetch user data
        fetchUserAlertsByPage();
        // Fetch data for the week
        fetchWeekData();
        // Fetch the Metric Data
        fetchConformanceData();
      }
    }, [amsUser]);

    const handleFilterCreated = () => {
      // Show your toast here (e.g., using notistack, MUI Snackbar, or your own)
      showToast("Custom Filter Successfully Created", "success");
      console.log("Filter created successfully");
    };

    async function clearSearch() {
      const token = await checkAuthToken();

      setFilterData(prev => ({
        filterTerms: filterData["filterTerms"],
        totalFilters: getFilterCount("", filterData["filterTerms"]),
        searchTerm: ""
      }));

      dispatch(
        setAlertsByPage(
          {
            target: "cases",
            aFAccessToken: token, 
            page: page,
            size: pageSize,
            orderBy: sortData.field,
            order: sortData.order,
            filters: filterData["filterTerms"],
            search: ""
          }
        )
      );
    }

    return(
      <div className={`${alertState.previewWidth === 100 ? "" : "ps-3 pb-3 pe-3"} flex-grow flex-shrink-1 overflow-scroll`} 
        style={{
          flexGrow: 0, 
          flexShrink: 1,
          width: `${alertState.previewWidth ? (100 - alertState.previewWidth) : 100}%`,
          transition: 'width 0.2s ease-in-out'
        }}>
        <GridPageLayout {...gridBox} />
        <div className="analystDash-cases-toolbar">
            <AnalystToolbar
              headCells={userCaseState.userHeaders}
              setFilterData={handleFilterData}
              isFilterModalOpen={isFilterModalOpen}
              setPage={setPage}
              setIsFilterModalOpen={setIsFilterModalOpen} 
              filterData={filterData}
              deleteFilter={deleteFilter}
              clearSearch={clearSearch}
              onFilterCreated={handleFilterCreated}

            />
        </div>
      
        <StyledDashContainer sideModel={openSideModal}>
          <DashboardColumn>
            <DashboardItem title={t("ANALYST_DASH.ASSIGNED_TO_ME")}>
                <Datagrid
                  rowData={userCaseState.userEvents}
                  headCells={userCaseState.userHeaders}
                  visibleKeys={userCaseState.userHeaders
                    .filter((header) => header.showColumnUnExpanded)
                    .map((header) => header.id)}
                  maxWidths={{
                      entity: 200,
                      classification: 200,
                  }}
                  showSelectionCheckbox={false}
                  showPagination={true}
                  pageSize={pageSize}
                  totalData={userCaseState.totalRowCount}
                  isFilterModalOpen={isFilterModalOpen}
                  setIsFilterModalOpen={setIsFilterModalOpen}
                  filterData={filterData}
                  onRowClick={onAlertClick}
                  highlightedRowId={userCaseState.selectedRow}
                  setFilterData={handleFilterData}
                  statusMapping={statusMapping}
                  handleSetStatus={handleSetStatus}
                  isPageLoading={userCaseState.areUserEventsLoading}
                  setPage={setPage}
                  page={page}
                  onRowsPerPageChange={handleRowsPerPageChange}
                  onPageChange={onPageChange}
                  onSortChange={onSortChange}
                />
            </DashboardItem>
            
            <DashboardItem title="Alert Trends (last 7 days)">
              <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center" }}>
                  <StackedAreaChart 
                      data={alertTrends}
                      theme={theme}
                  />
                  <div className="align-self-baseline">
                  <ChartLegend
                      flexDirection="row"
                      data={[
                      {
                          "text": "Fatal",
                          "color": theme.colors.fatal_criticality
                      },
                      {
                          "text": "Critical",
                          "color": theme.colors.critical_criticality
                      },
                      {
                          "text": "High",
                          "color": theme.colors.high_criticality
                      },
                      {
                          "text": "Medium",
                          "color": theme.colors.medium_criticality
                      },
                      {
                          "text": "Low",
                          "color": theme.colors.low_criticality
                      },
                      {
                          "text": "Informative",
                          "color": theme.colors.informative_criticality
                      },
                      {
                          "text": "Unclassified",
                          "color": theme.colors.unclassified_criticality
                      }
                      ]} 
                  />
                  </div>
              </Box>
            </DashboardItem>
            
            <DashboardItem title="Cases by urgency (last 7 days)">
              <Box className="m-0 d-flex justify-content-center flex-column">
                  <HorizontalBarChart 
                    theme={theme}
                    data={casesByUrgency.map((category) => ({
                        color: category.circleColor,
                        value: category.count,
                        name: category.description
                    }))}
                  />
              </Box>
            </DashboardItem>
            
            <DashboardItem title="Service Activity (last 7 Days)">
                <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap" }}>
                {/* Render CustomPieChart for Open */}
                <CustomPieChart
                    isRing={true}
                    text={t("STATUS.OPEN")}
                    hasNumber={true}
                    data={weekServiceActivity.map((category) => ({
                      color: category.circleColor,
                      name: category.description,
                      value: category.count
                    }))}
                />

                {/* Render CustomPieChart for In Progress */}
                <CustomPieChart
                    isRing={true}
                    text={t("STATUS.IN_PROGRESS")}
                    hasNumber={true}
                    data={weekServiceActivity.map((category) => ({
                      color: category.circleColor,
                      name: category.description,
                      value: category.count
                    }))}
                />

                {/* Render CustomPieChart for Closed */}
                <CustomPieChart
                    isRing={true}
                    text={t("STATUS.CLOSED")}
                    hasNumber={true}
                    data={weekServiceActivity.map((category) => ({
                    color: category.circleColor,
                    name: category.description,
                    value: category.count
                    }))}
                />

                {/* Render CustomPieChart for Request Tuning */}
                <CustomPieChart
                    isRing={true}
                    text={t("STATUS.REQUEST_TUNING")}
                    hasNumber={true}
                    data={weekServiceActivity.map((category) => ({
                    color: category.circleColor,
                    name: category.description,
                    value: category.count
                    }))}
                />
                </Box>

                <Box className="d-flex flex-row flex-wrap align-items-star pt-4">
                {weekServiceActivity.map((category, index) => (
                    <div className="d-flex align-items-center" key={index}>              
                    <CircleIcon
                        style={{
                            color: category.circleColor,
                            verticalAlign: "middle",
                            fontSize: 12,
                            marginRight: 5,
                            marginLeft: 12
                        }}
                    />
                    <div>{category.description}</div>
                    </div>
                ))}
                </Box>
            </DashboardItem>
          </DashboardColumn>
          <DashboardColumn>
            <DashboardItem title="Alert Prioritization Status">
                <Box>
                  <Box className="d-flex align-items-center flex-wrap">
                      <CustomPieChart 
                        isRing={true}
                        text="Overall Health" 
                        data={allServiceActivity.map((category) => ({
                            color: category.circleColor,
                            name: category.description,
                            value: category.count
                        }))} 
                      />
                      <ChartLegend
                        flexDirection="column"
                        data={allServiceActivity.map((category) => ({
                            color: category.circleColor,
                            text: category.description,
                            percentage: category.percentage
                        }))}
                      />
                  </Box>
                </Box>
            </DashboardItem>
            
            <DashboardItem title="Framework Compliance">
              <CompliancePieCharts
                isRing={true}
                hasNumber={true}
                text="Compliant"
                complianceData={userCaseState.conformanceMetrics}
              />
            </DashboardItem>
    

            {/* <DashboardItem title="Cyber Intelligence Feed">
                <CyberIntelligence threat_feeds={threat_feeds} reports={reports} theme={theme}/>
            </DashboardItem> */}

          </DashboardColumn>
          
        </StyledDashContainer>
      </div>
    )
}

export default AnalystTable;